// Builds the storefront and subscription rows the details panel always shows.

import { formatTimestamp } from './tool-call-format';
import type {
  LabelledValueDto,
  OrderItemDto,
  OrderItemRefundDto,
  OrderOtherRefundDto,
  SentinelAiEventLogWireDto,
  SubscriptionBlockDto,
  SubscriptionItemDto,
  TraceStep,
  WorkspaceOrderDto,
  WorkspaceStorefrontDto,
  WorkspaceSubscriptionDto,
} from './types';

/** The storefronts and subscription providers the panel always shows a row for. */
const STOREFRONT_PROVIDER = { provider: 'Shopify', logo: '/assets/brand/shopify.svg', accentColor: '#94bf45' };
/** Tool providers whose results the storefront card is allowed to read. */
const STOREFRONT_TOOL_PROVIDERS = ['shopify'];
const SUBSCRIPTION_PROVIDERS = [
  { provider: 'Recharge', logo: '/assets/brand/recharge-mark.svg', accentColor: '#3901f1' },
  { provider: 'Smartrr', logo: '/assets/brand/smartrr-mark.png', accentColor: '#3b1e33' },
  { provider: 'Skio', logo: '/assets/brand/skio.png', accentColor: '#462379' },
];

/** Every tool the subscription providers expose. None shares a name with a Shopify tool. */
const SUBSCRIPTION_TOOL_NAMES = new Set([
  'box_config_get', 'cancellation_reason_record', 'charge_apply_discount', 'charge_refund', 'charge_skip', 'customer_get',
  'onetime_create', 'product_variants_eligible_list', 'selling_plans_get', 'subscription_activate',
  'subscription_billing_date_update', 'subscription_box_line_item_add', 'subscription_box_variants_list', 'subscription_cancel',
  'subscription_change_address', 'subscription_detail_get', 'subscription_discount_apply', 'subscription_get',
  'subscription_interval_update', 'subscription_line_item_add', 'subscription_line_item_update', 'subscription_note_update',
  'subscription_pause', 'subscription_selling_plan_update', 'subscription_set_next_charge_date', 'subscription_ship_now',
  'subscription_skip',
]);

/** Always returns a storefront so the row stays visible; it is simply empty until data arrives. */
export function storefrontFromOrder(order: WorkspaceOrderDto | null, fromTrace: WorkspaceOrderDto[] = []): WorkspaceStorefrontDto {
  const seen = new Set(order ? [order.externalId] : []);
  const extra = fromTrace.filter((entry) => !seen.has(entry.externalId) && seen.add(entry.externalId));
  const orders = order ? [order, ...extra] : extra;
  return {
    ...STOREFRONT_PROVIDER,
    totalOrders: orders.length,
    totalSpent: orders[0]?.totalSpent ?? '—',
    customerSince: null,
    tags: [],
    orders,
    drafts: [],
  };
}

/** Keeps known provider rows; API data wins and trace data fills gaps. */
export function subscriptionPanels(sent: SubscriptionBlockDto[], fromTrace: SubscriptionBlockDto[] = []): WorkspaceSubscriptionDto[] {
  return SUBSCRIPTION_PROVIDERS.map((known) => {
    const byProvider = (block: SubscriptionBlockDto): boolean => block.provider.toLowerCase() === known.provider.toLowerCase();
    const match = sent.find(byProvider) ?? fromTrace.find(byProvider);
    return { ...known, count: 0, totalSpent: '—', meta: [], items: [], ...match };
  });
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function money(amount: unknown, currency: unknown): string | null {
  if (amount === null || amount === undefined || amount === '') return null;
  return typeof currency === 'string' && currency ? `${currency} ${amount}` : String(amount);
}

/** Some tools put the "#" in the order id and some do not, so every builder keys it the same way. */
function orderKey(value: unknown): string {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  const text = String(value).trim();
  if (!text) return '';
  return text.startsWith('#') ? text : `#${text}`;
}

/** Collapses snake_case, camelCase and Title Case onto one lookup key. */
function lookupKey(key: string): string {
  return key.toLowerCase().replaceAll(/[^a-z0-9]/g, '');
}

function flatten(record: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) out[lookupKey(key)] = value;
  return out;
}

function pick(record: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) if (record[key] !== undefined && record[key] !== null) return record[key];
  return undefined;
}

/** Fields only an order carries — enough to tell one from a customer or a page of results. */
const ORDER_MARKERS = [
  'lineitems',
  'totalprice',
  'financialstatus',
  'displayfulfillmentstatus',
  'fulfillmentstatus',
  'fulfillments',
  'orderid',
  'shippingaddress',
];

/** A return or subscription result carries an order id too, so only the storefront's own tools count. */
function feedsStorefront(step: TraceStep): boolean {
  // An unlabelled subscription step (Recharge's customer_get lists orders) must not reach the Shopify card.
  if (step.toolName && SUBSCRIPTION_TOOL_NAMES.has(step.toolName)) return false;
  return !step.toolProvider || STOREFRONT_TOOL_PROVIDERS.includes(step.toolProvider.toLowerCase());
}

function looksLikeOrder(flat: Record<string, unknown>): boolean {
  const name = flat['name'];
  if (typeof name === 'string' && name.startsWith('#')) return true;
  return ORDER_MARKERS.some((marker) => flat[marker] !== undefined);
}

/** Shopify's restock type says what a refund did to an item: taken off before shipping, sent back, or just refunded. */
function refundKind(restockType: unknown): OrderItemRefundDto['kind'] {
  const type = typeof restockType === 'string' ? restockType.toUpperCase() : '';
  return type === 'CANCEL' ? 'removed' : type === 'RETURN' ? 'returned' : 'refunded';
}

function sameItemName(left: string, right: string): boolean {
  const key = (name: string): string => name.trim().toLowerCase().replace(/\s+/g, ' ');
  return key(left) === key(right);
}

/**
 * Marks the lines each refund covered. Shopify's refund items carry a name but no line id, so they
 * match by name, filling the latest matching line first. A refund with no item (compensation), or an
 * item no line has room for, is kept as an order-level refund so the card still shows it.
 */
function applyRefunds(items: OrderItemDto[], rawRefunds: unknown): { items: OrderItemDto[]; otherRefunds: OrderOtherRefundDto[] } {
  const lines = items.map((item) => ({ ...item, refunds: [...(item.refunds ?? [])] }));
  const otherRefunds: OrderOtherRefundDto[] = [];
  for (const entry of Array.isArray(rawRefunds) ? rawRefunds : []) {
    const refund = readRecord(entry);
    if (!refund) continue;
    const flat = flatten(refund);
    const created = pick(flat, 'createdat');
    const date = typeof created === 'string' && created ? formatTimestamp(created.split('T')[0]!, 'long') : null;
    const note = typeof flat['note'] === 'string' && flat['note'].trim() ? flat['note'].trim() : null;
    let matchedAny = false;
    const unmatched: string[] = [];
    for (const raw of Array.isArray(flat['items']) ? flat['items'] : []) {
      // The event log cuts deep values short and leaves a string in their place; those are skipped.
      const item = readRecord(raw);
      if (!item) continue;
      const flatItem = flatten(item);
      // Same field order as the order lines, so the two names come from the same Shopify field.
      const name = String(pick(flatItem, 'title', 'name') ?? '');
      const kind = refundKind(flatItem['restocktype']);
      let remaining = typeof flatItem['quantity'] === 'number' && flatItem['quantity'] > 0 ? flatItem['quantity'] : 1;
      for (let index = lines.length - 1; index >= 0 && remaining > 0; index -= 1) {
        const line = lines[index]!;
        if (!sameItemName(line.name, name)) continue;
        const room = line.quantity - line.refunds.reduce((sum, covered) => sum + covered.quantity, 0);
        if (room <= 0) continue;
        const quantity = Math.min(room, remaining);
        line.refunds.push({ kind, quantity, date, note });
        remaining -= quantity;
        matchedAny = true;
      }
      if (remaining > 0) unmatched.push(`${name || 'Item'} ×${remaining}`);
    }
    if (unmatched.length > 0 || !matchedAny) otherRefunds.push({ date, note, items: unmatched });
  }
  return {
    items: lines.map(({ refunds, ...item }) => (refunds.length > 0 ? { ...item, refunds } : item)),
    otherRefunds,
  };
}

/** How much one copy of an order says, so the fullest of several lookups of it is the one kept. */
function orderDetailScore(order: WorkspaceOrderDto): number {
  return (
    order.items.length +
    order.statuses.length +
    (order.shippingAddress ? 1 : 0) +
    (order.shipment ? 1 : 0) +
    order.items.filter((item) => item.refunds).length +
    (order.otherRefunds?.length ?? 0)
  );
}

/** Builds an order from any tool result that carries one, whatever the tool named its fields. */
function orderFromToolOutput(output: Record<string, unknown>): WorkspaceOrderDto | null {
  const flat = flatten(output);
  if (!looksLikeOrder(flat)) return null;

  const id = pick(flat, 'orderid', 'id');
  const name = flat['name'];
  const externalId = orderKey(typeof name === 'string' && name ? name : id);
  if (!externalId) return null;

  const address = readRecord(pick(flat, 'shippingaddress'));
  // Shopify sends `tracking` as a list of {number, url, company}; the first shipment that has one is shown.
  const shipments = pick(flat, 'tracking');
  const tracking =
    readRecord(pick(flat, 'trackingdetails')) ??
    (Array.isArray(shipments) ? readRecord(shipments.find((entry) => readRecord(entry)?.['url'] || readRecord(entry)?.['number'])) : null);
  const rawItems = pick(flat, 'lineitems', 'items');
  const lineItems = Array.isArray(rawItems) ? rawItems : [];
  const created = pick(flat, 'createdat', 'orderedon');
  const statuses = [pick(flat, 'financialstatus'), pick(flat, 'status', 'fulfillmentstatus', 'displayfulfillmentstatus')]
    .filter((value): value is string => typeof value === 'string' && value.trim() !== '')
    .map((value) => value.toUpperCase());

  const { items, otherRefunds } = applyRefunds(
    lineItems.flatMap((entry) => {
      const item = readRecord(entry);
      if (!item) return [];
      const flatItem = flatten(item);
      return [{
        quantity: typeof flatItem['quantity'] === 'number' ? flatItem['quantity'] : 1,
        name: String(pick(flatItem, 'title', 'name') ?? 'Item'),
        price: String(pick(flatItem, 'price') ?? '—'),
      }];
    }),
    pick(flat, 'refunds'),
  );

  return {
    externalId,
    statuses,
    orderedOn: typeof created === 'string' ? formatTimestamp(created.split('T')[0]!, 'long') : '—',
    items,
    ...(otherRefunds.length > 0 ? { otherRefunds } : {}),
    shippingCost: null,
    shippingAddress: address && (() => {
      const flatAddress = flatten(address);
      return {
        name: String(pick(flatAddress, 'name') ?? '—'),
        address1: String(pick(flatAddress, 'address1') ?? '—'),
        address2: String(pick(flatAddress, 'address2') ?? '—'),
        city: String(pick(flatAddress, 'city') ?? '—'),
        country: String(pick(flatAddress, 'country') ?? '—'),
        zipcode: String(pick(flatAddress, 'zip', 'zipcode', 'postalcode') ?? '—'),
      };
    })(),
    shipment: tracking && (() => {
      const flatTracking = flatten(tracking);
      return {
        provider: String(pick(flatTracking, 'company', 'provider', 'carrier') ?? '—'),
        trackingId: String(pick(flatTracking, 'number', 'trackingid', 'trackingnumber') ?? '—'),
        link: String(pick(flatTracking, 'url', 'trackingurl', 'link') ?? ''),
      };
    })(),
    totalSpent: money(pick(flat, 'totalprice', 'totalspent'), pick(flat, 'currency')),
  };
}

/** An approval prompt labels its fields for a human; these are the order fields behind them. */
const APPROVAL_ORDER_LABELS: Record<string, string> = {
  order: 'name',
  ordername: 'name',
  ordernumber: 'name',
  requestedordernumber: 'name',
  orderid: 'orderid',
  shopifyorderid: 'orderid',
  amount: 'totalprice',
  ordertotal: 'totalprice',
  total: 'totalprice',
  fulfillmentstatus: 'fulfillmentstatus',
  financialstatus: 'financialstatus',
  orderstatus: 'status',
  createdat: 'createdat',
};

/** An approval's rows are labelled for a human; rename them to the keys the builders read. */
function approvalFields(details: Record<string, unknown>, labels: Record<string, string>): Record<string, unknown> {
  const fields: Record<string, unknown> = {};
  for (const entry of Array.isArray(details['items']) ? details['items'] : []) {
    const row = readRecord(entry);
    const key = typeof row?.['label'] === 'string' ? labels[lookupKey(row['label'])] : undefined;
    if (!key || fields[key] !== undefined) continue;
    const value = row!['value'];
    // Every approval value arrives as text, but quantities are read as numbers.
    fields[key] = key === 'quantity' && typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value;
  }
  return fields;
}

/** The line items an approval listed, whatever it called the group. */
function approvalLineItems(details: Record<string, unknown>): unknown[] {
  return (Array.isArray(details['groups']) ? details['groups'] : []).flatMap((entry) => {
    const group = readRecord(entry);
    return Array.isArray(group?.['items']) ? group['items'] : [];
  });
}

/** Rebuilds the order an approval described, so a HITL ticket shows it with no lookup tool. */
function orderFromApproval(request: Record<string, unknown>): WorkspaceOrderDto | null {
  const details = readRecord(request['details']);
  if (!details) return null;
  // No order identity means this approval described something else, like a subscription.
  return orderFromToolOutput({ ...approvalFields(details, APPROVAL_ORDER_LABELS), lineitems: approvalLineItems(details) });
}

/** Every approval prompt on the ticket, pending or already answered. */
function approvalRequests(events: SentinelAiEventLogWireDto[]): Record<string, unknown>[] {
  return events.flatMap((event) => {
    const approvals = event.approval_requests;
    if (!approvals) return [];
    return ['requests', 'resolved_requests'].flatMap((key) =>
      (Array.isArray(approvals[key]) ? (approvals[key] as unknown[]) : []).flatMap((entry) => {
        const request = readRecord(entry);
        return request ? [request] : [];
      }),
    );
  });
}

/** Orders the approval prompts on this ticket described. */
export function ordersFromApprovals(events: SentinelAiEventLogWireDto[]): WorkspaceOrderDto[] {
  const byId = new Map<string, WorkspaceOrderDto>();
  for (const request of approvalRequests(events)) {
    const order = orderFromApproval(request);
    if (!order) continue;
    const existing = byId.get(order.externalId);
    // Two prompts can name one order, so keep whichever says more.
    if (!existing || orderDetailScore(order) > orderDetailScore(existing)) {
      byId.set(order.externalId, order);
    }
  }
  return [...byId.values()];
}

/** Shopify's update input, in the shape the order card renders. */
function addressFromUpdate(input: Record<string, unknown>): NonNullable<WorkspaceOrderDto['shippingAddress']> {
  const flat = flatten(input);
  const name = ['firstname', 'lastname'].map((key) => flat[key]).filter((part) => typeof part === 'string' && part).join(' ');
  return {
    name: name || '—',
    address1: String(pick(flat, 'address1') ?? '—'),
    address2: String(pick(flat, 'address2') ?? '—'),
    city: String(pick(flat, 'city') ?? '—'),
    country: String(pick(flat, 'country') ?? '—'),
    zipcode: String(pick(flat, 'zip', 'zipcode', 'postalcode') ?? '—'),
  };
}

/** The order an approval names, keyed the way every other builder keys one. */
function approvalOrderKey(details: Record<string, unknown>): string | null {
  const fields = approvalFields(details, APPROVAL_ORDER_LABELS);
  const name = fields['name'];
  return orderKey(typeof name === 'string' && name ? name : fields['orderid']) || null;
}

/** The lookup runs before the change, so an approved update holds the newer address. */
// Drop this once the workflow re-reads the order: that lookup carries what Shopify actually stored.
export function withApprovedAddresses(
  storefront: WorkspaceStorefrontDto,
  events: SentinelAiEventLogWireDto[],
  trace: TraceStep[],
): WorkspaceStorefrontDto {
  if (!trace.some((step) => step.toolName === 'orders_update' && step.outcome === 'success')) return storefront;
  const byOrder = new Map<string, NonNullable<WorkspaceOrderDto['shippingAddress']>>();
  for (const request of approvalRequests(events)) {
    if (request['tool_name'] !== 'orders_update' || request['decision'] !== 'approve') continue;
    const address = readRecord(readRecord(readRecord(request['parameters'])?.['input'])?.['shippingAddress']);
    const details = readRecord(request['details']);
    const key = details && approvalOrderKey(details);
    // A later approval supersedes an earlier one on the same order.
    if (address && key) byOrder.set(key, addressFromUpdate(address));
  }
  if (byOrder.size === 0) return storefront;
  return {
    ...storefront,
    orders: storefront.orders.map((order) => {
      const address = byOrder.get(order.externalId);
      return address ? { ...order, shippingAddress: address } : order;
    }),
  };
}

/** Shopify cancels through a job, so the reply carries no order state; the approval names the order. */
// Drop this once the workflow re-reads the order.
export function withCancelledOrders(
  storefront: WorkspaceStorefrontDto,
  events: SentinelAiEventLogWireDto[],
  trace: TraceStep[],
): WorkspaceStorefrontDto {
  if (!trace.some((step) => step.toolName === 'orders_cancel' && step.outcome === 'success')) return storefront;
  const cancelled = new Set<string>();
  for (const request of approvalRequests(events)) {
    if (request['tool_name'] !== 'orders_cancel' || request['decision'] !== 'approve') continue;
    const details = readRecord(request['details']);
    const key = details && approvalOrderKey(details);
    if (key) cancelled.add(key);
  }
  if (cancelled.size === 0) return storefront;
  return {
    ...storefront,
    orders: storefront.orders.map((order) =>
      cancelled.has(order.externalId) ? { ...order, statuses: ['CANCELLED'] } : order,
    ),
  };
}

/** A subscription cancel returns the status it wrote, so the card shows the real one. */
export function withCancelledSubscriptions(
  subscriptions: WorkspaceSubscriptionDto[],
  trace: TraceStep[],
): WorkspaceSubscriptionDto[] {
  const statusById = new Map<string, string>();
  for (const step of trace) {
    if (step.toolName !== 'subscription_cancel' || step.outcome !== 'success') continue;
    const flat = flatten(readRecord(step.toolOutput) ?? {});
    const id = pick(flat, 'subscriptionid', 'id');
    // Skio returns only { id, success }, so a successful cancel with no status means cancelled.
    const status = typeof flat['status'] === 'string' && flat['status'] ? flat['status'] : flat['success'] === true ? 'cancelled' : null;
    // A later cancel on the same plan wins.
    if (id !== undefined && status) statusById.set(String(id), status.toUpperCase());
  }
  if (statusById.size === 0) return subscriptions;
  return subscriptions.map((subscription) => ({
    ...subscription,
    items: subscription.items.map((item) => {
      const status = statusById.get(item.id);
      return status ? { ...item, status } : item;
    }),
  }));
}

/** The subscription fields an approval prompt spells out for the agent. */
const APPROVAL_SUBSCRIPTION_LABELS: Record<string, string> = {
  subscriptionid: 'subscriptionid',
  status: 'status',
  currentstatus: 'status',
  product: 'producttitle',
  quantity: 'quantity',
  price: 'price',
  nextchargedate: 'nextchargescheduledat',
  cancelledat: 'cancelledat',
};

/** Rebuilds the subscription an approval described. */
function subscriptionFromApproval(request: Record<string, unknown>): SubscriptionItemDto | null {
  const details = readRecord(request['details']);
  if (!details) return null;
  const items = approvalLineItems(details).flatMap((entry) => {
    const item = flatten(readRecord(entry) ?? {});
    // The builder reads products by their product keys, but an approval just calls it a name.
    return item['name'] === undefined ? [] : [{ producttitle: item['name'], price: item['price'], quantity: item['quantity'] }];
  });
  // Approval prices are already formatted, so they are never in minor units.
  return subscriptionFromToolOutput({ ...approvalFields(details, APPROVAL_SUBSCRIPTION_LABELS), items }, false);
}

/** The brand's subscription integration decides; without one, the step's own label; older traces fall back to the payload. */
function subscriptionProviderOf(step: TraceStep, connected?: string[]): string | null {
  // An account connects one subscription provider, so its integration names the provider. Two (internal accounts) fall through.
  const integrations = connected && SUBSCRIPTION_PROVIDERS.filter((known) => connected.includes(known.provider.toLowerCase()));
  if (integrations?.length === 1) {
    return step.toolName && SUBSCRIPTION_TOOL_NAMES.has(step.toolName) ? integrations[0]!.provider : null;
  }
  const slug = step.toolProvider?.toLowerCase();
  // No subscription integration: only a provider the step names, never a guess from its text.
  if (integrations?.length === 0) return SUBSCRIPTION_PROVIDERS.find((known) => known.provider.toLowerCase() === slug)?.provider ?? null;
  const named = SUBSCRIPTION_PROVIDERS.find((known) => known.provider.toLowerCase() === slug);
  // A step that names another provider is not a subscription step, even if its result mentions one.
  if (slug) return named?.provider ?? null;
  const text = JSON.stringify(step.toolOutput) ?? '';
  return SUBSCRIPTION_PROVIDERS.find((known) => new RegExp(known.provider, 'i').test(text))?.provider ?? null;
}

/** An approval names no provider, so the card it joins is the one the ticket's tools named. */
function subscriptionProviderFromTrace(trace: TraceStep[], connected?: string[]): string | null {
  for (const step of trace) {
    const provider = subscriptionProviderOf(step, connected);
    if (provider) return provider;
  }
  return null;
}

/** Subscriptions the approval prompts on this ticket described. */
export function subscriptionsFromApprovals(
  events: SentinelAiEventLogWireDto[],
  trace: TraceStep[],
  connected?: string[],
): SubscriptionBlockDto[] {
  const provider = subscriptionProviderFromTrace(trace, connected);
  if (!provider) return [];
  const byId = new Map<string, SubscriptionItemDto>();
  for (const request of approvalRequests(events)) {
    const subscription = subscriptionFromApproval(request);
    // A later prompt describes the same plan after the action, so let it win.
    if (subscription) byId.set(subscription.id, subscription);
  }
  const items = [...byId.values()];
  return items.length ? [{ provider, count: items.length, totalSpent: '—', meta: [], items }] : [];
}

/** Identifies subscription-shaped records without relying on tool names. */
function looksLikeSubscription(flat: Record<string, unknown>): boolean {
  // Recharge and Smartrr expose different subscription identifiers.
  if (flat['nextchargescheduledat'] !== undefined || flat['nextbillingdate'] !== undefined) return true;
  if (flat['subscriptionid'] !== undefined && flat['status'] !== undefined) return true;
  return flat['status'] !== undefined && (flat['producttitle'] !== undefined || flat['planname'] !== undefined);
}

/** Smartrr prices arrive in minor units ("8100" for $81.00); other providers send decimals. */
function minorUnitsToAmount(value: string, minorUnits: boolean): string {
  return minorUnits && /^\d+$/.test(value) ? (Number(value) / 100).toFixed(2) : value;
}

/** Skio sends prices as numbers (7.8), so they get two decimals; text and minor-unit prices go through as before. */
function amountText(value: unknown, minorUnits: boolean): string {
  if (typeof value === 'number' && !minorUnits) return value.toFixed(2);
  return minorUnitsToAmount(String(value ?? ''), minorUnits);
}

/** Skio sends the cadence as an interval and a count, e.g. WEEK and 4: "Every 4 weeks". */
function billingFrequency(flat: Record<string, unknown>): string | null {
  const unit = flat['billinginterval'];
  const count = flat['billingintervalcount'];
  if (typeof unit !== 'string' || !unit || typeof count !== 'number') return null;
  const word = unit.toLowerCase();
  return count === 1 ? `Every ${word}` : `Every ${count} ${word}s`;
}

function countValue(value: unknown): string | null {
  return typeof value === 'number' ? String(value) : null;
}

function dateValue(value: unknown): string | null {
  return typeof value === 'string' ? formatTimestamp(value.split('T')[0]!, 'long') : null;
}

/** Maps a subscription-shaped tool result to the details-panel model. */
function subscriptionFromToolOutput(record: Record<string, unknown>, minorUnits: boolean): SubscriptionItemDto | null {
  const flat = flatten(record);
  if (!looksLikeSubscription(flat)) return null;
  const id = pick(flat, 'subscriptionid', 'id');
  if (id === undefined) return null;

  const created = pick(flat, 'createdat', 'subscreated');
  const cancelled = pick(flat, 'cancelledat', 'cancellationdate');
  const reason = pick(flat, 'cancellationreason');
  const url = pick(flat, 'skiourl', 'smartrrurl');
  const currency = pick(flat, 'currency', 'currencycode');
  // Smartrr keeps the products on nested line items and titles the plan by its program.
  const lineItems = pick(flat, 'items', 'lineitems');
  const products = (Array.isArray(lineItems) ? lineItems : []).flatMap((entry) => {
    const flatItem = flatten(readRecord(entry) ?? {});
    const name = pick(flatItem, 'productname', 'producttitle', 'title');
    if (typeof name !== 'string') return [];
    const variant = pick(flatItem, 'productvarianttitle', 'varianttitle');
    return [{
      name: typeof variant === 'string' && variant ? `${name} · ${variant}` : name,
      quantity: typeof flatItem['quantity'] === 'number' ? flatItem['quantity'] : null,
      amount: money(amountText(pick(flatItem, 'price', 'amount'), minorUnits), pick(flatItem, 'currency') ?? currency) ?? '—',
    }];
  });
  const firstItem = Array.isArray(lineItems) ? flatten(readRecord(lineItems[0]) ?? {}) : {};
  const rawAmount = pick(flat, 'price', 'amount') ?? pick(firstItem, 'price', 'amount');
  const details = [
    { label: 'Next Billing Date', value: dateValue(pick(flat, 'nextbillingdate', 'nextchargescheduledat')) },
    { label: 'Plan', value: (pick(flat, 'subscriptionplan') as string | undefined) ?? billingFrequency(flat) },
  ].flatMap((row) => (row.value ? [{ label: row.label, value: row.value }] : []));
  return {
    id: String(id),
    name: String(
      pick(flat, 'subscriptionprogram', 'producttitle', 'planname', 'name') ??
        pick(firstItem, 'productname', 'producttitle', 'title') ??
        'Subscription',
    ),
    status: String(pick(flat, 'status') ?? 'UNKNOWN').toUpperCase(),
    createdOn: dateValue(created) ?? '—',
    quantity: typeof flat['quantity'] === 'number' ? flat['quantity'] : typeof firstItem['quantity'] === 'number' ? firstItem['quantity'] : null,
    amount:
      money(amountText(rawAmount, minorUnits), currency ?? pick(firstItem, 'currency')) ?? '—',
    amountLabel: 'Amount',
    details,
    ...(products.length > 0 ? { products } : {}),
    // Only https links, since the value comes from tool output and becomes an href.
    ...(typeof url === 'string' && url.startsWith('https://') ? { url } : {}),
    cancellationReason: typeof reason === 'string' && reason ? reason : null,
    cancellationDate: dateValue(cancelled),
  };
}

/** Account rows shown above the plans — read from the customer record beside them. */
function accountMetaFromRecord(record: Record<string, unknown>): { meta: LabelledValueDto[]; totalSpent: string | null } {
  const flat = flatten(record);
  const isSkioCustomer = flat['totalsubscriptions'] !== undefined;
  if (flat['rechargeurl'] === undefined && flat['processortype'] === undefined && flat['activesubscription'] === undefined && !isSkioCustomer) {
    return { meta: [], totalSpent: null };
  }
  const processor = pick(flat, 'processortype');
  const meta = [
    { label: 'Customer Since', value: dateValue(pick(flat, 'createdat')) },
    { label: 'Updated at', value: dateValue(pick(flat, 'updatedat')) },
    { label: 'Processor type', value: typeof processor === 'string' && processor ? processor : null },
    // Skio pages its list, so the totals say how many plans exist beyond the ones shown.
    { label: 'Active subscriptions', value: countValue(pick(flat, 'activesubscriptions')) },
    { label: 'Total subscriptions', value: countValue(pick(flat, 'totalsubscriptions')) },
  ].flatMap((row) => (row.value ? [{ label: row.label, value: row.value }] : []));
  // No provider sends a spend aggregate today; wired so it shows the day one does.
  return { meta, totalSpent: money(pick(flat, 'totalspent'), pick(flat, 'currency')) };
}

/** Reads subscription data from provider-marked tool output. */
export function subscriptionsFromTrace(trace: TraceStep[], connected?: string[]): SubscriptionBlockDto[] {
  const byProvider = new Map<string, { items: Map<string, SubscriptionItemDto>; meta: LabelledValueDto[]; totalSpent: string | null }>();

  let minorUnits = false;
  const collect = (value: unknown, bucket: { items: Map<string, SubscriptionItemDto>; meta: LabelledValueDto[]; totalSpent: string | null }, depth = 0): void => {
    if (depth > 4) return;
    if (Array.isArray(value)) {
      for (const entry of value) collect(entry, bucket, depth + 1);
      return;
    }
    const record = readRecord(value);
    if (!record) return;
    const item = subscriptionFromToolOutput(record, minorUnits);
    // Later trace steps carry the newer subscription state.
    if (item) bucket.items.set(item.id, item);
    else if (bucket.meta.length === 0) {
      const account = accountMetaFromRecord(record);
      bucket.meta = account.meta;
      bucket.totalSpent = bucket.totalSpent ?? account.totalSpent;
    }
    for (const nested of Object.values(record)) collect(nested, bucket, depth + 1);
  };

  for (const step of trace) {
    if (step.toolOutput === undefined) continue;
    const provider = subscriptionProviderOf(step, connected);
    if (!provider) continue;
    minorUnits = provider === 'Smartrr';
    const bucket = byProvider.get(provider) ?? { items: new Map<string, SubscriptionItemDto>(), meta: [], totalSpent: null };
    collect(step.toolOutput, bucket);
    if (bucket.items.size > 0) byProvider.set(provider, bucket);
  }

  return [...byProvider.entries()].map(([provider, bucket]) => ({
    provider,
    count: bucket.items.size,
    totalSpent: bucket.totalSpent ?? '—',
    meta: bucket.meta,
    items: [...bucket.items.values()],
  }));
}

/**
 * What the TOOL CALLS list shows: every step no provider card absorbed, so data from a
 * provider without a card is never dropped.
 */
/** A pause from an older run can never resolve, so only the active request's pauses count. */
function isStalePause(step: TraceStep, activeRequestId?: string | null): boolean {
  if (step.outcome !== 'awaiting_approval' || !activeRequestId || !step.id.includes(':')) return false;
  return !step.id.startsWith(`${activeRequestId}:`);
}

export function visibleTraceSteps(trace: TraceStep[], activeRequestId?: string | null, connected?: string[]): TraceStep[] {
  const claimed = providerClaimedStepIds(trace, connected);
  return trace.filter((step) => !claimed.has(step.id) && !isStalePause(step, activeRequestId));
}

export function providerClaimedStepIds(trace: TraceStep[], connected?: string[]): Set<string> {
  const claimed = new Set<string>();
  const holdsOrder = (value: unknown, depth = 0): boolean => {
    if (depth > 4) return false;
    if (Array.isArray(value)) return value.some((entry) => holdsOrder(entry, depth + 1));
    const record = readRecord(value);
    if (!record) return false;
    if (orderFromToolOutput(record)) return true;
    return Object.values(record).some((nested) => holdsOrder(nested, depth + 1));
  };
  const holdsSubscription = (value: unknown, depth = 0): boolean => {
    if (depth > 4) return false;
    if (Array.isArray(value)) return value.some((entry) => holdsSubscription(entry, depth + 1));
    const record = readRecord(value);
    if (!record) return false;
    if (subscriptionFromToolOutput(record, false)) return true;
    return Object.values(record).some((nested) => holdsSubscription(nested, depth + 1));
  };
  for (const step of trace) {
    if (step.toolOutput === undefined) continue;
    if (feedsStorefront(step) && holdsOrder(step.toolOutput)) {
      claimed.add(step.id);
      continue;
    }
    // Subscriptions only reach a card when the integration or the step names their provider.
    if (subscriptionProviderOf(step, connected) && holdsSubscription(step.toolOutput)) {
      claimed.add(step.id);
    }
  }
  return claimed;
}

export function ordersFromTrace(trace: TraceStep[]): WorkspaceOrderDto[] {
  const byId = new Map<string, WorkspaceOrderDto>();

  /** Walks a tool result looking for anything order-shaped, wherever the tool nested it. */
  const collect = (value: unknown, depth = 0): void => {
    if (depth > 4) return;
    if (Array.isArray(value)) {
      for (const entry of value) collect(entry, depth + 1);
      return;
    }
    const record = readRecord(value);
    if (!record) return;

    const order = orderFromToolOutput(record);
    if (order) {
      const existing = byId.get(order.externalId);
      // A detailed lookup carries more than a list entry, so keep whichever says more.
      if (!existing || orderDetailScore(order) > orderDetailScore(existing)) {
        byId.set(order.externalId, order);
      }
    }
    // Keep descending regardless: a wrapper may hold several orders below it.
    for (const nested of Object.values(record)) collect(nested, depth + 1);
  };

  for (const step of trace) {
    if (feedsStorefront(step)) collect(step.toolOutput);
  }
  return [...byId.values()];
}
